<template>
  <SDialog :open="isOpen" @update:open="onOpenChange">
    <SDialogContent
      :show-close-button="false"
      class="!fixed !inset-0 !top-0 !left-0 !m-0 !h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 !gap-0 !rounded-none !border-0 !p-0 !grid-rows-[auto_1fr] overflow-hidden"
      data-cy="check-in-page"
      @open-auto-focus.prevent
      @pointer-down-outside.prevent
      @focus-outside.prevent
    >
      <!-- Header, always visible -->
      <SDialogHeader
        class="!space-y-0 !text-left bg-[#004c53] px-4 py-3 text-white"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex min-w-0 items-center gap-3">
            <button
              type="button"
              class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md bg-white/10 hover:bg-white/20"
              :aria-label="$t('button.back')"
              data-cy="check-in-back"
              @click="onClose"
            >
              <i
                class="s-icon s-icon--chevron-left size-5"
                style="--s-icon-size: 20px"
                aria-hidden="true"
              />
            </button>

            <div class="min-w-0">
              <p class="truncate text-xs text-white/70">{{ getAreaLine }}</p>
              <SDialogTitle
                class="truncate text-xl font-bold text-white"
                data-cy="check-in-title"
              >
                {{ getTitle }}
              </SDialogTitle>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-3">
            <span
              class="rounded-full bg-white/15 px-3 py-1.5 text-sm whitespace-nowrap"
              data-cy="check-in-counts"
            >
              {{
                $t("check-in.checked-in-count", {
                  done: lines.checkedInCount.value,
                  total: lines.totalCount.value,
                })
              }}
            </span>

            <span
              class="flex size-9 items-center justify-center rounded-md bg-white/10"
              :title="$t('check-in.scanner-ready')"
              data-cy="check-in-scanner-ready"
            >
              <i
                class="s-icon s-icon--scan-line size-5"
                style="--s-icon-size: 20px"
                aria-hidden="true"
              />
            </span>

            <button
              type="button"
              class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-white/20"
              :aria-label="$t('button.close')"
              data-cy="check-in-close"
              @click="onClose"
            >
              <i
                class="s-icon s-icon--x size-5"
                style="--s-icon-size: 20px"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </SDialogHeader>

      <div class="flex min-h-0 w-full min-w-0 flex-col bg-gray-50">
        <!-- What the operator can act on right now -->
        <div
          class="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-3"
        >
          <label class="flex cursor-pointer items-center gap-3">
            <SCheckbox
              :model-value="getIsAllSelected"
              :disabled="!lines.eligibleLines.value.length"
              data-cy="check-in-select-all"
              @update:model-value="onSelectAll"
            />
            <span
              class="text-sm text-gray-600"
              data-cy="check-in-selection-summary"
            >
              {{
                getSelectedCount
                  ? $t("check-in.selected-summary", {
                      lines: getSelectedLines.length,
                      count: getSelectedCount,
                    })
                  : $t("check-in.select-all-eligible")
              }}
            </span>
          </label>

          <SButton
            type="button"
            size="lg"
            :disabled="!getCanCheckIn || getSelectedCount === 0"
            data-cy="check-in-selected"
            @click="onCheckInSelected"
          >
            <i
              class="s-icon s-icon--check"
              style="--s-icon-size: 16px"
              aria-hidden="true"
            />
            {{ $t("check-in.check-in-selected") }}
          </SButton>
        </div>

        <div
          class="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-6"
        >
          <p
            v-if="!CheckInModule.areaId"
            class="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-cy="check-in-no-area"
          >
            <i class="s-icon s-icon--triangle-alert" aria-hidden="true" />
            {{ $t("check-in.no-area-warning") }}
          </p>

          <p v-if="!getIsOnline" class="text-sm" data-cy="check-in-offline">
            <i class="s-icon s-icon--wifi-off" aria-hidden="true" />
            {{ $t("check-in.offline") }}
          </p>

          <p
            v-else-if="lines.isWaitingForSync.value"
            class="px-6 py-4 text-sm"
            data-cy="check-in-not-synced"
          >
            {{ $t("check-in.not-synced") }}
          </p>

          <p
            v-else-if="lines.hasError.value"
            class="px-6 py-4 text-sm text-red-700"
            data-cy="check-in-load-error"
          >
            <i class="s-icon s-icon--triangle-alert" aria-hidden="true" />
            {{ $t("check-in.load-failed") }}
          </p>

          <p
            v-else-if="lines.isLoading.value"
            class="px-6 py-4 text-sm text-gray-500"
            data-cy="check-in-loading"
          >
            {{ $t("common.loading") }}
          </p>

          <p
            v-else-if="!lines.eligibleLines.value.length"
            class="rounded-md border border-gray-200 bg-white px-6 py-8 text-center text-sm text-gray-500"
            data-cy="check-in-empty"
          >
            {{ getEmptyText }}
          </p>

          <div
            v-else
            class="w-full min-w-0 divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200 bg-white"
            data-cy="check-in-list"
          >
            <div v-for="line in lines.eligibleLines.value" :key="line.key">
              <div class="flex items-center gap-4 px-4 py-3">
                <SCheckbox
                  :model-value="getIsLineSelected(line)"
                  :aria-label="line.categoryName"
                  data-cy="check-in-line-select"
                  @update:model-value="onToggleLine(line)"
                />

                <div class="min-w-0 flex-1">
                  <p class="truncate font-semibold text-gray-900">
                    {{ line.categoryName }} - {{ line.priceName }}
                  </p>
                  <p
                    class="text-sm"
                    :class="line.checkedIn ? 'text-amber-600' : 'text-gray-500'"
                  >
                    {{
                      $t("check-in.checked-in-count", {
                        done: line.checkedIn,
                        total: line.total,
                      })
                    }}
                    · {{ getSlotLabel(line.startDateTime) }}
                  </p>

                  <button
                    type="button"
                    class="mt-0.5 flex cursor-pointer items-center gap-1 text-sm font-medium text-teal-700 hover:underline"
                    data-cy="check-in-toggle-tickets"
                    @click="onToggleTickets(line)"
                  >
                    <i
                      class="s-icon size-4"
                      :class="
                        getIsExpanded(line)
                          ? 's-icon--chevron-down'
                          : 's-icon--chevron-right'
                      "
                      style="--s-icon-size: 14px"
                      aria-hidden="true"
                    />
                    {{
                      getIsExpanded(line)
                        ? $t("check-in.hide-tickets")
                        : $t("check-in.show-tickets")
                    }}
                  </button>
                </div>

                <div class="flex shrink-0 items-center gap-3">
                  <CheckInQuantityCell
                    :model-value="getLineQuantity(line)"
                    :max="line.open"
                    :disabled="!getCanCheckIn"
                    @change="onQuantityChange(line.key, $event)"
                  />

                  <SButton
                    type="button"
                    size="lg"
                    class="w-40 bg-blue-600 text-white hover:bg-blue-700"
                    :disabled="!getCanCheckIn || line.open === 0"
                    data-cy="check-in-line-action"
                    @click="onCheckInLine(line)"
                  >
                    <i
                      class="s-icon s-icon--check size-4"
                      style="--s-icon-size: 14px"
                      aria-hidden="true"
                    />
                    {{
                      $t("check-in.check-in-n", {
                        count: getLineQuantity(line),
                      })
                    }}
                  </SButton>
                </div>
              </div>

              <!-- One row for each ticket of the line -->
              <div
                v-if="getIsExpanded(line)"
                class="divide-y divide-gray-100 border-t border-gray-100"
              >
                <div
                  v-for="ticket in line.tickets"
                  :key="ticket.id"
                  class="flex items-center gap-4 py-2.5 pr-4 pl-14"
                  :class="ticket.isCheckedIn ? 'bg-green-50' : 'bg-gray-50'"
                  data-cy="check-in-ticket-row"
                >
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm text-gray-900">
                      {{ ticket.priceName }}
                      <span v-if="ticket.seat" class="text-gray-500">
                        · {{ ticket.seat }}
                      </span>
                    </p>
                    <p class="font-mono text-xs text-gray-400">
                      {{ getTicketReference(ticket) }}
                    </p>
                  </div>

                  <div
                    v-if="ticket.isCheckedIn"
                    class="flex shrink-0 items-center gap-3"
                  >
                    <span
                      class="flex items-center gap-1 text-sm font-medium text-green-700"
                      data-cy="check-in-ticket-state"
                    >
                      <i
                        class="s-icon s-icon--check size-4"
                        style="--s-icon-size: 14px"
                        aria-hidden="true"
                      />
                      {{ getTicketScanLabel(ticket) }}
                    </span>

                    <SButton
                      type="button"
                      variant="outline"
                      size="sm"
                      :disabled="!getCanCheckIn"
                      data-cy="check-in-ticket-undo"
                      @click="onUndoTicket(ticket)"
                    >
                      {{ $t("check-in.undo") }}
                    </SButton>
                  </div>

                  <SButton
                    type="button"
                    v-else
                    variant="outline"
                    size="sm"
                    :disabled="!getCanCheckIn"
                    data-cy="check-in-ticket-action"
                    @click="onCheckInTicket(ticket)"
                  >
                    <i
                      class="s-icon s-icon--check size-4"
                      style="--s-icon-size: 14px"
                      aria-hidden="true"
                    />
                    {{ $t("check-in.title") }}
                  </SButton>
                </div>
              </div>
            </div>
          </div>

          <!-- Tickets outside their scan window. The operator cannot send them. -->
          <div
            v-if="lines.notOpenLines.value.length"
            class="mt-4 rounded-md border border-gray-200 bg-white px-4 py-3"
            data-cy="check-in-later"
          >
            <p class="text-sm font-semibold text-gray-900">
              {{ $t("check-in.not-open-title") }}
            </p>
            <p
              v-for="line in lines.notOpenLines.value"
              :key="line.key"
              class="mt-1 text-sm text-gray-500"
            >
              {{ line.categoryName }} - {{ line.priceName }} ·
              {{ getSlotLabel(line.startDateTime) }} · {{ line.total }}
              <span class="font-medium text-amber-600">
                · {{ getTimingLabel(line) }}
              </span>
            </p>
          </div>

          <!-- What the engine answered -->
          <div v-if="getShowResults" class="mt-4" data-cy="check-in-results">
            <p
              v-if="isSuccessVisible"
              class="text-sm font-semibold text-green-700"
              data-cy="check-in-success"
            >
              <i class="s-icon s-icon--circle-check" aria-hidden="true" />
              {{ $t("check-in.success", { count: getSuccessCount }) }}
            </p>

            <p
              v-for="failure in getErrors"
              :key="failure.key"
              class="text-sm text-red-700"
              data-cy="check-in-failure"
            >
              <i class="s-icon s-icon--triangle-alert" aria-hidden="true" />
              {{ failure.categoryName }} ({{ failure.count }}):
              {{ failure.message }}
            </p>

            <div
              v-if="getNotForNow.length"
              class="mt-2"
              data-cy="check-in-not-now"
            >
              <p class="text-sm font-semibold">
                {{ $t("check-in.not-used-now") }}
              </p>
              <p
                v-for="failure in getNotForNow"
                :key="failure.key"
                class="text-sm text-gray-500"
              >
                {{ failure.categoryName }} ({{ failure.count }}):
                {{ failure.message }}
              </p>
            </div>
          </div>

          <p
            v-if="scanMessage"
            class="mt-2 text-sm"
            data-cy="check-in-scan-message"
          >
            {{ scanMessage }}
          </p>

          <p
            v-if="action.errorMessage.value"
            class="mt-2 text-sm text-red-700"
            data-cy="check-in-error"
          >
            {{ action.errorMessage.value }}
          </p>
        </div>

        <p
          v-if="CheckInModule.areaName"
          class="shrink-0 border-t border-gray-200 bg-white px-6 py-3 text-xs text-gray-500"
        >
          {{ $t("check-in.footer-note", { area: CheckInModule.areaName }) }}
        </p>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts">
/**
 * Check-in page for one order.
 *
 * The modal host shows it when the status is CheckIn. The caller puts the
 * order in the modal properties.
 *
 * @component
 * @example
 * ModalModule.setModalProps({ props: { reference, title } });
 * ModalModule.setStatus(ModalStatus.CheckIn);
 */
export default { name: "CheckInPage" };
</script>

<script lang="ts" setup>
// common
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";

// modules
import CheckInModule from "@/store/modules/check-in";
import ModalModule from "@/store/modules/modal";
import ScannerModule from "@/store/modules/scanner";
import UserModule from "@/store/modules/user";

// composables
import {
  ICheckInLine,
  ICheckInTicket,
  LineTiming,
  useRedeemableLines,
} from "./composables/useRedeemableLines";
import { useCheckInAction } from "./composables/useCheckInAction";
import {
  OutcomeSeverity,
  useCheckInOutcomes,
} from "./composables/useCheckInOutcomes";

// helpers
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { reloadSelectedOrderTokens } from "@/utils/vue-helpers/orders.helper";
import { attachedVueElement, detachVueElement } from "@/utils/helpers/scanner";
import { LogService } from "@/services/logging";

// types
import { CheckInOutcome, IRedeemResult, ScanLogAction } from "@/types/check-in";
import { CheckInModalProps, ModalStatus } from "@/constants/modal";

// components
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

import CheckInQuantityCell from "./cells/CheckInQuantityCell.vue";

import "@smeetz/ui-icons/check/style.css";
import "@smeetz/ui-icons/chevron-down/style.css";
import "@smeetz/ui-icons/chevron-left/style.css";
import "@smeetz/ui-icons/chevron-right/style.css";
import "@smeetz/ui-icons/circle-check/style.css";
import "@smeetz/ui-icons/triangle-alert/style.css";
import "@smeetz/ui-icons/scan-line/style.css";
import "@smeetz/ui-icons/wifi-off/style.css";
import "@smeetz/ui-icons/x/style.css";

// One action with more than this many tickets asks for a confirmation.
const LARGE_ACTION_SIZE = 100;

// The green confirmation goes away without an action from the operator.
const SUCCESS_VISIBLE_MS = 4000;

const isOpen = dialogVisibleForStatus(ModalStatus.CheckIn);
const { t } = useI18n({ useScope: "global" });
const lines = useRedeemableLines();
const action = useCheckInAction();
const outcomes = useCheckInOutcomes();

const quantities = reactive<Record<string, number>>({});
const expanded = ref(new Set<string>());
const selectedKeys = ref(new Set<string>());
const isOnline = ref(navigator.onLine);
const scanMessage = ref("");
const isSuccessVisible = ref(false);

let successTimer: ReturnType<typeof setTimeout> | null = null;
let isScannerAttached = false;

const getOrder = computed(
  () => (ModalModule.modalProps?.props || {}) as CheckInModalProps
);

const getReference = computed(() => getOrder.value.reference || "");

const getTitle = computed(() => getOrder.value.title || t("check-in.title"));

const getIsOnline = computed(() => isOnline.value);

// The engine needs the order synced to it and an answer from the network.
const getCanCheckIn = computed(
  () =>
    getIsOnline.value &&
    lines.isSynced.value &&
    !!lines.orderId.value &&
    !action.isRunning.value
);

const getAreaLine = computed(() =>
  CheckInModule.areaName
    ? t("check-in.area-line", { area: CheckInModule.areaName })
    : t("check-in.no-area-line")
);

const getSuccessCount = computed(
  () =>
    action.results.value.filter(
      (result) => result.outcome === CheckInOutcome.Ok
    ).length
);

const getFailures = computed(() => {
  const ticketsById = new Map(
    lines.tickets.value.map((ticket) => [ticket.id, ticket])
  );

  return outcomes.groupFailures(action.results.value, ticketsById);
});

// A ticket for another date is not an error. It gets its own quiet section.
const getErrors = computed(() =>
  getFailures.value.filter(
    (failure) => failure.severity === OutcomeSeverity.Error
  )
);

const getNotForNow = computed(() =>
  getFailures.value.filter(
    (failure) => failure.severity === OutcomeSeverity.Info
  )
);

const getShowResults = computed(
  () =>
    isSuccessVisible.value ||
    getErrors.value.length > 0 ||
    getNotForNow.value.length > 0
);

// The store changes this value after every scan.
const getScannerTick = computed(() => ScannerModule.tog);

// Only a line with a ticket left can go to the engine.
const getSelectedLines = computed(() =>
  lines.eligibleLines.value.filter(
    (line) => line.open > 0 && selectedKeys.value.has(line.key)
  )
);

const getIsAllSelected = computed(() => {
  const selectable = lines.eligibleLines.value.filter((line) => line.open > 0);

  return (
    selectable.length > 0 && selectable.length === getSelectedLines.value.length
  );
});

const getSelectedCount = computed(() =>
  getSelectedLines.value.reduce(
    (total, line) => total + getLineQuantity(line),
    0
  )
);

const getEmptyText = computed(() => {
  // The order holds tickets, but all of them are for another date.
  if (lines.totalCount.value > 0) {
    return t("check-in.none-eligible-now");
  }

  // The order holds tickets, but none of them gives access.
  if (lines.rawCount.value > 0) {
    return t("check-in.no-access-tickets");
  }

  return t("check-in.nothing-to-check-in");
});

/**
 * Close the dialog.
 *
 * @param {boolean} open - The new open state of the dialog.
 * @returns {void}
 */
function onOpenChange(open: boolean): void {
  if (!open) {
    ModalModule.setStatus(ModalStatus.Hidden);
  }
}

/**
 * Keep the quantity of a line inside its open count.
 *
 * @param {string} key - Key of the line.
 * @param {number} value - The quantity the operator selected.
 * @returns {void}
 */
function onQuantityChange(key: string, value: number): void {
  quantities[key] = Number(value) || 0;
}

/**
 * Say whether a line is selected.
 *
 * @param {ICheckInLine} line - The line to read.
 * @returns {boolean} True when the operator selected it.
 */
function getIsLineSelected(line: ICheckInLine): boolean {
  return selectedKeys.value.has(line.key);
}

/**
 * Select or clear one line.
 *
 * @param {ICheckInLine} line - The line the operator clicked.
 * @returns {void}
 */
function onToggleLine(line: ICheckInLine): void {
  const next = new Set(selectedKeys.value);

  if (next.has(line.key)) {
    next.delete(line.key);
  } else {
    next.add(line.key);
  }

  selectedKeys.value = next;
}

/**
 * Select every line that can go to the engine, or clear them all.
 *
 * @returns {void}
 */
function onSelectAll(): void {
  selectedKeys.value = getIsAllSelected.value
    ? new Set<string>()
    : new Set(
        lines.eligibleLines.value
          .filter((line) => line.open > 0)
          .map((line) => line.key)
      );
}

/**
 * Say whether the tickets of a line are open.
 *
 * @param {ICheckInLine} line - The line to read.
 * @returns {boolean} True when its tickets are on screen.
 */
function getIsExpanded(line: ICheckInLine): boolean {
  return expanded.value.has(line.key);
}

/**
 * Show or hide the tickets of a line.
 *
 * @param {ICheckInLine} line - The line the operator clicked.
 * @returns {void}
 */
function onToggleTickets(line: ICheckInLine): void {
  const next = new Set(expanded.value);

  if (next.has(line.key)) {
    next.delete(line.key);
  } else {
    next.add(line.key);
  }

  expanded.value = next;
}

/**
 * Take back the entry of one ticket, then read the engine again.
 *
 * The row follows the engine and not the answer of the call, so a reversal
 * that the engine did not record leaves the ticket checked in and says so.
 *
 * @param {ICheckInTicket} ticket - The ticket to take back.
 * @returns {Promise<void>}
 */
async function onUndoTicket(ticket: ICheckInTicket): Promise<void> {
  scanMessage.value = "";
  action.errorMessage.value = "";

  const orderId = lines.orderId.value;

  if (!orderId) {
    return;
  }

  try {
    const undone = await action.undo(orderId, ticket.id);
    await lines.refreshScans();
    publishCounts();
    void reloadSelectedOrderTokens(getReference.value);

    const stillIn = lines.tickets.value.find((t) => t.id === ticket.id);

    if (!undone || stillIn?.isCheckedIn) {
      action.errorMessage.value = t("check-in.undo-failed");
    }
  } catch (error) {
    LogService.info("Check-in: the entry was not taken back", { error });
    action.errorMessage.value = t("check-in.undo-failed");
  }
}

/**
 * Check in one ticket of a line.
 *
 * @param {ICheckInTicket} ticket - The ticket to send.
 * @returns {Promise<void>}
 */
async function onCheckInTicket(ticket: ICheckInTicket): Promise<void> {
  await sendTickets([ticket.id]);
}

/**
 * Say when a ticket came in, and who let it in.
 *
 * @param {ICheckInTicket} ticket - The ticket to describe.
 * @returns {string} The text for the operator.
 */
function getTicketScanLabel(ticket: ICheckInTicket): string {
  if (!ticket.lastScanAt) {
    return t("check-in.checked-in");
  }

  const time = dayjs(ticket.lastScanAt).format("HH:mm");

  return ticket.lastScanBy
    ? t("check-in.checked-in-at-by", { time, name: ticket.lastScanBy })
    : t("check-in.checked-in-at", { time });
}

/**
 * Get the name of the operator at the terminal.
 *
 * @returns {string} The name to show against the entries they record.
 */
function getOperatorName(): string {
  const user = UserModule.getUser;

  return [user?.firstName, user?.lastName].filter(Boolean).join(" ");
}

/**
 * Close the page.
 *
 * @returns {void}
 */
function onClose(): void {
  ModalModule.setStatus(ModalStatus.Hidden);
}

/**
 * Check in one line.
 *
 * @param {ICheckInLine} line - The line to check in.
 * @returns {Promise<void>}
 */
async function onCheckInLine(line: ICheckInLine): Promise<void> {
  await sendTickets(openTicketsOf(line, getLineQuantity(line)));
}

/**
 * Check in the selected lines, each with its own quantity.
 *
 * @returns {Promise<void>}
 */
async function onCheckInSelected(): Promise<void> {
  const tokenIds = getSelectedLines.value.flatMap((line) =>
    openTicketsOf(line, getLineQuantity(line))
  );

  await sendTickets(tokenIds);
}

/**
 * Check in one ticket that the operator scanned.
 *
 * @param {string} value - The value that the scanner read.
 * @returns {Promise<void>}
 */
async function onScan(value: string): Promise<void> {
  scanMessage.value = "";

  const code = tokenFromScan(value);
  const known = lines.tickets.value.find((ticket) => ticket.token === code);

  // A ticket of this order that is in already stops here (TC12).
  if (known?.isCheckedIn) {
    scanMessage.value = await lastScanText(known.id);

    return;
  }

  const result = await action.checkInByBarCode(code);

  if (!result) {
    return;
  }

  // The operator stays on this order. A ticket of another order, or one let
  // in with no order (a ticket of another booking), shows "other order". A
  // code that resolves no ticket at all gets the engine's own message
  // ("ticket not found"), which says more than "not in this order".
  if (
    !result.orderId ||
    !result.tokenId ||
    result.orderId !== lines.orderId.value
  ) {
    scanMessage.value =
      result.orderId || result.outcome === CheckInOutcome.Ok
        ? t("check-in.other-order")
        : outcomes.messageFor({
            tokenId: "",
            outcome: result.outcome,
            message: result.message,
            scannedAt: null,
          });

    return;
  }

  const scanResult: IRedeemResult = {
    tokenId: result.tokenId,
    outcome: result.outcome,
    message: result.message,
    scannedAt: result.scannedAt,
  };

  lines.applyResults([scanResult], getOperatorName());
  publishCounts();
  void reloadSelectedOrderTokens(getReference.value);
  action.results.value = [scanResult];
  scanMessage.value = outcomes.messageFor(scanResult);
  showSuccess();
}

/**
 * Say when a line becomes available, or why it never will today.
 *
 * @param {ICheckInLine} line - The line to describe.
 * @returns {string} The reason for the operator.
 */
function getTimingLabel(line: ICheckInLine): string {
  if (line.timing === LineTiming.Ended) {
    return t("check-in.ended");
  }

  if (line.timing === LineTiming.NotOpenYet && line.opensAt) {
    return t("check-in.opens-at", {
      time: dayjs(line.opensAt).format("HH:mm"),
    });
  }

  return t("check-in.not-for-today");
}

/**
 * Get the ticket token from a scan. POS prints the token alone on a ticket,
 * and a JSON object with the token and the code on a voucher.
 *
 * @param {string} value - The value that the scanner read.
 * @returns {string} The token to send to the engine.
 */
function tokenFromScan(value: string): string {
  const scanned = value.trim();

  if (!scanned.startsWith("{")) {
    return scanned;
  }

  try {
    return JSON.parse(scanned).token || scanned;
  } catch (error) {
    LogService.info("Check-in: the scanned value is not valid JSON", { error });

    return scanned;
  }
}

/**
 * Start every line at its open count and clear the selection.
 *
 * @returns {void}
 */
function resetSelection(): void {
  for (const key of Object.keys(quantities)) {
    delete quantities[key];
  }

  for (const line of lines.lines.value) {
    quantities[line.key] = line.open;
  }

  selectedKeys.value = new Set<string>();
}

/**
 * Get the text for a ticket that is already in. The engine keeps the time
 * and the operator of every scan, one ticket for each call.
 *
 * @param {string} tokenId - The ticket to read.
 * @returns {Promise<string>} The text for the operator.
 */
async function lastScanText(tokenId: string): Promise<string> {
  const orderId = lines.orderId.value;

  if (!orderId) {
    return t("check-in.already-checked-in");
  }

  try {
    const history = await action.readTokenHistory(orderId, tokenId);
    const lastScan = [...history.scanLogs]
      .reverse()
      .find((log) => log.action === ScanLogAction.Scan);

    if (!lastScan) {
      return t("check-in.already-checked-in");
    }

    const name = [
      lastScan.scanningUser?.firstName,
      lastScan.scanningUser?.lastName,
    ]
      .filter(Boolean)
      .join(" ");

    if (!name) {
      return t("check-in.checked-in-at", {
        time: dayjs(lastScan.createdAt).format("HH:mm"),
      });
    }

    return t("check-in.checked-in-at-by", {
      time: dayjs(lastScan.createdAt).format("HH:mm"),
      name,
    });
  } catch (error) {
    LogService.info("Check-in: the scan history did not load", { error });

    return t("check-in.already-checked-in");
  }
}

/**
 * Show the green confirmation, then hide it again.
 *
 * @returns {void}
 */
function showSuccess(): void {
  if (successTimer) {
    clearTimeout(successTimer);
  }

  isSuccessVisible.value = getSuccessCount.value > 0;

  if (!isSuccessVisible.value) {
    return;
  }

  successTimer = setTimeout(() => {
    isSuccessVisible.value = false;
  }, SUCCESS_VISIBLE_MS);
}

/**
 * Get the quantity of a line. A line starts with all its open tickets.
 *
 * @param {ICheckInLine} line - The line to read.
 * @returns {number} The quantity to check in.
 */
function getLineQuantity(line: ICheckInLine): number {
  const quantity = quantities[line.key];

  return quantity === undefined ? line.open : Math.min(quantity, line.open);
}

/**
 * Get the ids of the tickets that are still open on a line.
 *
 * @param {ICheckInLine} line - The line to read.
 * @param {number} count - How many tickets the operator wants.
 * @returns {string[]} The token ids to send.
 */
function openTicketsOf(line: ICheckInLine, count: number): string[] {
  return line.tickets
    .filter((ticket) => !ticket.isCheckedIn)
    .slice(0, count)
    .map((ticket) => ticket.id);
}

/**
 * Send tickets to the engine, then show the answer.
 *
 * @param {string[]} tokenIds - The tickets to check in.
 * @returns {Promise<void>}
 */
async function sendTickets(tokenIds: string[]): Promise<void> {
  const orderId = lines.orderId.value;

  if (!tokenIds.length || !orderId || !getCanCheckIn.value) {
    return;
  }

  if (tokenIds.length > LARGE_ACTION_SIZE) {
    const confirmed = await showConfirm({
      title: t("common.warning"),
      message: t("check-in.confirm-large", { count: tokenIds.length }),
      variant: "warning",
      confirmText: t("common.yes"),
      cancelText: t("common.no"),
    });

    if (!confirmed) {
      return;
    }
  }

  const results = await action.checkIn(orderId, tokenIds);

  lines.applyResults(results, getOperatorName());
  publishCounts();
  void reloadSelectedOrderTokens(getReference.value);
  resetSelection();
  showSuccess();
}

/**
 * Hand the counts of this order to the Orders table. The ticket index needs
 * a moment to catch up, and the operator must see the entry straight away.
 *
 * @returns {void}
 */
function publishCounts(): void {
  const reference = getReference.value;

  if (!reference) {
    return;
  }

  CheckInModule.setOrderCount({
    reference,
    checkedIn: lines.checkedInCount.value,
    total: lines.totalCount.value,
  });
}

/**
 * Get the date and time of a slot. A ticket with no date has no slot.
 *
 * @param {string | null} startDateTime - Start of the slot.
 * @returns {string} The text for the operator.
 */
function getSlotLabel(startDateTime: string | null): string {
  return startDateTime
    ? dayjs(startDateTime).format("DD/MM/YYYY HH:mm")
    : t("check-in.no-date");
}

/**
 * Get a short reference of a ticket, so the operator can read it aloud.
 *
 * @param {ICheckInTicket} ticket - The ticket to read.
 * @returns {string} The last part of the token.
 */
function getTicketReference(ticket: ICheckInTicket): string {
  return ticket.token.slice(-6).toUpperCase();
}

/**
 * Track the network state. The engine needs a connection.
 *
 * @returns {void}
 */
function updateOnlineState(): void {
  isOnline.value = navigator.onLine;
}

watch(
  () => [isOpen.value, getReference.value],
  async ([open, reference]) => {
    if (!open || !reference) {
      return;
    }

    action.results.value = [];
    action.errorMessage.value = "";

    try {
      await lines.load(String(reference));
      expanded.value = new Set<string>();
      resetSelection();
    } catch (error) {
      LogService.info("Check-in: the tickets did not load", { error });
    }
  },
  { immediate: true }
);

// The basket also reads the scanner. It stops while this page is a listener.
watch(
  () => isOpen.value,
  (open) => {
    if (open && !isScannerAttached) {
      attachedVueElement();
      isScannerAttached = true;

      return;
    }

    if (!open && isScannerAttached) {
      detachVueElement();
      isScannerAttached = false;
    }

    // A closed page stops waiting for its order.
    if (!open) {
      lines.stop();
    }
  },
  { immediate: true }
);

watch(getScannerTick, async () => {
  const code = ScannerModule.value;

  if (!isOpen.value || !code || !getCanCheckIn.value) {
    return;
  }

  await onScan(code);
});

onMounted(() => {
  window.addEventListener("online", updateOnlineState);
  window.addEventListener("offline", updateOnlineState);
});

onBeforeUnmount(() => {
  window.removeEventListener("online", updateOnlineState);
  window.removeEventListener("offline", updateOnlineState);

  if (successTimer) {
    clearTimeout(successTimer);
  }

  if (isScannerAttached) {
    detachVueElement();
    isScannerAttached = false;
  }
});
</script>
